/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { timediff } from '@marc-maniac/timediff';
import * as datefns from 'date-fns';
import { format as dateFormat } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import _ from 'lodash';
import numeral from 'numeral';
import { frequencyTdMap, } from './models';
dayjs.extend(utc);
export const round = (value, precision) => {
    const multiplier = 10 ** (precision || 0);
    return Math.round(value * multiplier) / multiplier;
};
const parseSemverTriplet = (version) => {
    const match = version.match(/^(\d+)\.(\d+)\.(\d+)/);
    if (!match) {
        return null;
    }
    return match.slice(1).map(Number);
};
export const compareSemverTriplet = (version, reference) => {
    const parsedVersion = parseSemverTriplet(version);
    const parsedReference = parseSemverTriplet(reference);
    if (!parsedVersion || !parsedReference) {
        return null;
    }
    for (const [i, referencePart] of parsedReference.entries()) {
        const versionPart = parsedVersion[i] ?? 0;
        if (versionPart !== referencePart) {
            return versionPart - referencePart;
        }
    }
    return 0;
};
export const format = (value, precision = 2, scale = 1, defaultVal = null, returnDashIfZero = true, wrapNegativesInParanthesis = false, percent = false, magnitudeChar = false) => {
    if (returnDashIfZero && value === 0) {
        return '-';
    }
    if (value !== undefined && value !== null) {
        const scaledVal = value / scale;
        if (magnitudeChar && (scaledVal >= 10000 || scaledVal <= 10000)) {
            const precisionDigits = new Array(precision).fill('0').join('');
            return numeral(scaledVal).format(`(0.${precisionDigits} a)`);
        }
        const formatted = round(scaledVal, precision ?? 6).toLocaleString(undefined, {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        });
        if (wrapNegativesInParanthesis && value < 0)
            return `(${formatted.slice(1)})`;
        if (percent) {
            let operator;
            if (value > 0) {
                operator = '+';
            }
            else {
                operator = '';
            }
            return `${operator + formatted}%`;
        }
        return formatted;
    }
    if (defaultVal === null) {
        return '';
    }
    return defaultVal.toFixed(precision ?? 6).toLocaleString();
};
export const prettifySnakeCase = (str, capitalize = true) => {
    if (!str)
        return '';
    return str
        .split('_')
        .map((word) => (capitalize ? _.capitalize(word) : word))
        .join(' ');
};
export const getSalesforceRecord = (id, component) => {
    if (!id) {
        return null;
    }
    for (const x of component) {
        if (x.Id === id || x.Id?.slice(0, -3) === id) {
            return x;
        }
    }
    return null;
};
// TODO: replace with `getRecord`
export const getComponent = (id, component) => {
    if (!id) {
        return null;
    }
    for (const x of component) {
        // Handle idKey check here
        if (x.id === id) {
            return x;
        }
    }
    return null;
};
export const getRecord = ({ r, idKey = 'id', id, }) => {
    if (!id) {
        return null;
    }
    for (const x of r) {
        // Handle idKey check here
        if (x[idKey] === id) {
            return x;
        }
    }
    return null;
};
export const getVersionedComponent = (id, component) => {
    if (!id) {
        return null;
    }
    for (const x of component) {
        if (x.id === id[0] && x.version === id[1]) {
            return x;
        }
    }
    return null;
};
export function fimoTsToAnnual(fimoStart, ts) {
    const startDate = fromZonedTime(new Date(fimoStart), 'UTC');
    const startYear = startDate.getFullYear();
    const dates = Object.entries(ts)[0][1].map((_, i) => datefns.endOfMonth(datefns.add(startDate, { months: i })));
    const years = new Set();
    const annualTimeseries = { ...ts };
    dates.forEach((date) => {
        years.add(date.getFullYear());
    });
    Object.entries(annualTimeseries).forEach(([key]) => {
        const tsEntry = key;
        const xy = ts[tsEntry]?.map((y, x) => ({ x, y })) ?? [];
        const valsPerYear = Object.values(_.groupBy(xy, (point) => dates[point.x].getFullYear() - startYear));
        // bit of a hack, probably needs to be addressed at some point
        const aggregator = !key.includes('prices') ? _.sumBy : _.meanBy;
        annualTimeseries[tsEntry] = valsPerYear.map((xyForYear) => aggregator(xyForYear, (point) => point.y));
    });
    return { years: Array.from(years), ts: annualTimeseries };
}
export function fimoTsToQuarterly(fimoStart, ts) {
    const startDate = fromZonedTime(new Date(fimoStart), 'UTC');
    const dates = Object.entries(ts)[0][1].map((_, i) => datefns.endOfMonth(datefns.add(startDate, { months: i })));
    const quarters = [];
    const quarterlyTimeseries = { ...ts };
    dates.forEach((date) => {
        const endOfQuarter = datefns.endOfQuarter(date);
        if (quarters.find((d) => datefns.isEqual(d, endOfQuarter)) === undefined) {
            quarters.push(endOfQuarter);
        }
    });
    Object.entries(quarterlyTimeseries).forEach(([key]) => {
        const tsEntry = key;
        quarterlyTimeseries[tsEntry] = [];
        const quarterValues = quarterlyTimeseries[tsEntry];
        ts[tsEntry]?.forEach((value, index) => {
            const quarterlyIndex = quarters.findIndex((d) => datefns.isEqual(d, datefns.endOfQuarter(dates[index])));
            if (quarterValues !== undefined) {
                quarterValues[quarterlyIndex] = (quarterValues[quarterlyIndex] ?? 0) + value;
            }
        });
    });
    return { dates: quarters, ts: quarterlyTimeseries };
}
export function formatConfigName(config, truncateComment, hideVersion = false) {
    if (!config) {
        return '';
    }
    const versionStr = hideVersion ? '' : ` v${config.version}`;
    let output = `${'config_name' in config ? config.config_name : config.name}${versionStr}`;
    if ('comment' in config && config.comment && truncateComment) {
        output += ` - ${config.comment.slice(0, truncateComment)}`;
    }
    return output;
}
export function getConfigPath(type, config) {
    if (!config) {
        return '';
    }
    const id = 'id' in config ? config.id : config.config_id;
    const params = `config=${id}&version=${config.version}`;
    switch (type) {
        case 'fimo':
            return `/fimo/configurations/new?${params}`;
        case 'pvsim':
            return `/pv/pvsim/simulate?${params}`;
        case 'pvcosting':
            return `/pv/costing/simulate?${params}`;
        case 'esscosting':
            return `/ess/costing/simulate?${params}`;
        case 'plantsim':
            return `/plant/simulate?${params}`;
        case 'plantsim-v2':
            return `/plant-v2/simulate?${params}`;
        default:
            return '';
    }
}
export const transpose = (data) => data.length > 0 && Array.isArray(data[0]) ? data[0].map((_, i) => data.map((row) => row[i])) : [];
export const parseCsvString = (s, skipCol = 0, removeEmptyCells = false) => {
    let matrix = s.split('\n').map((row) => {
        // split on commas that are not inside quotes
        const parts = row.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/);
        return parts.map((part) => 
        // trim spaces then strip a single pair of surrounding quotes
        part.trim().replace(/^"(.*)"$/, '$1'));
    });
    if (removeEmptyCells) {
        matrix = matrix.filter(Boolean);
    }
    const transposed = transpose(matrix.map((row) => row.slice(skipCol, row.length)));
    return Object.fromEntries(transposed.map((col) => {
        let sliced = col.slice(1).filter(Boolean);
        if (!Number.isNaN(Number(sliced[0]))) {
            sliced = sliced.map(Number);
        }
        return [col[0].trim(), sliced];
    }));
};
export const formatDate = (d, f = 'YYYY-MM-DD HH:mm') => {
    if (d === null || d === '')
        return '';
    return dayjs(d).utc(false).format(f);
};
export function formatDateLocal(date, omitTime = false) {
    if (date === null) {
        return '';
    }
    let dateObj;
    if (date) {
        dateObj = date instanceof Date ? date : new Date(date);
    }
    else {
        dateObj = new Date();
    }
    return dateFormat(dateObj, omitTime ? 'P' : 'Pp');
}
export const prepareConfigId = (uuid, version) => {
    if (!uuid || !version) {
        return null;
    }
    return [uuid, version];
};
export function fromObjectEntries(e) {
    return Object.fromEntries(e);
}
export function objectEntries(o) {
    return Object.entries(o ?? {});
}
export function objectKeys(o) {
    return Object.keys(o ?? {});
}
export function objectValues(o) {
    return Object.values(o ?? {});
}
export const filterObject = (o, ...keys) => (o
    ? Object.fromEntries(Object.entries(o).filter(([key]) => !keys.includes(key)))
    : undefined);
export const filterCollection = (c, ...keys) => c?.map((o) => filterObject(o, ...keys));
export const shallowDiff = (fromObj, toObj) => _.fromPairs(_.differenceWith(_.toPairs(fromObj), _.toPairs(toObj), _.isEqual));
export const listToString = (l) => {
    if (!l)
        return '';
    if (l.length === 1) {
        return l[0];
    }
    return `${l.slice(0, -1).join(', ')}${l.length > 2 ? ',' : ''} and ${l.at(-1)}`;
};
export const getElementFromPossibleList = (x, index = 0) => {
    return Array.isArray(x) ? x[index] : x;
};
export function formatPercent(value, precision = 2, scale = 1) {
    if (value === undefined || value === null) {
        return '';
    }
    const precisionDigits = new Array(precision).fill('0').join('');
    return numeral(value / scale).format(`0.${precisionDigits}%`);
}
const removeTrailingLeadingChar = (str, char) => {
    let subS = str;
    if (subS.startsWith(char)) {
        subS = subS.slice(1);
    }
    if (subS.endsWith(char)) {
        subS = subS.slice(0, -1);
    }
    return subS;
};
export const slugFolderName = (name) => {
    const slugged = name
        .replace(/\//g, '') // remove /
        .replace(/\?/g, '') // remove ?
        .replace(/\|/g, '') // remove |
        .replace(/>/g, '') // remove >
        .replace(/</g, '') // remove <
        .replace(/:/g, '') // remove :
        .replace(/"/g, '') // remove "
        .replace(/%/g, 'pct') // replace % with pct
        .trim(); // remove trailing/leading spaces
    return removeTrailingLeadingChar(slugged, '.');
};
export function typedInvert(o) {
    return _.invert(o);
}
export const constructArray = (length, fillVal) => new Array(length).fill(fillVal);
export const calculateWeightedAverage = (values, weights) => {
    const totals = values.map((v, i) => v * weights[i]);
    const weightedAvg = _.sum(totals) / _.sum(weights);
    return Number.isNaN(weightedAvg) ? 0 : weightedAvg;
};
export const getArrayProp = (arr, k, defaultVal) => arr?.map((x) => _.get(x, k)) ?? defaultVal;
export function parsePercent(input, elseVal = '') {
    if (input === null || input === undefined) {
        return elseVal;
    }
    return round(input * 100, 4).toString();
}
export function timeDelta(weather_start_date, weather_end_date, frequency) {
    const td = timediff(weather_start_date, datefns.add(new Date(weather_end_date), { minutes: frequencyTdMap[frequency] }));
    return [
        ...(td.years ? [`${td.years}y`] : []),
        ...(td.months ? [`${td.months}mo${td.months > 1 ? 's' : ''}`] : []),
        ...(td.weeks ? [`${td.weeks}w`] : []),
        ...(td.days ? [`${td.days}d`] : []),
        ...(td.hours ? [`${td.hours}h`] : []),
        ...(td.minutes ? [`${td.minutes}min${td.minutes > 1 ? 's' : ''}`] : []),
    ].join(' ');
}
export const scale = (value, scalingValue, roundPrecision = 2) => {
    if (value == null || value === undefined) {
        return value;
    }
    return round(value * scalingValue, roundPrecision);
};
// biome-ignore lint/suspicious/noExplicitAny: generic type
export const getMaybeCallbackValue = (v, ...args) => typeof v === 'function' ? v(...args) : v;
export const getArrayFromMaybeArray = (arr) => Array.isArray(arr) ? arr : [arr];
export const isCloseTo = (value, reference, tolerance = 1e-4) => {
    if (typeof tolerance === 'number') {
        return Math.abs(value - reference) < tolerance;
    }
    const { rtol = 1e-5, atol = 1e-8 } = tolerance;
    return Math.abs(value - reference) <= atol + rtol * Math.abs(reference);
};
export const includesLiteral = (values, value) => values.includes(value);
//# sourceMappingURL=utils.js.map